import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, Outlet } from 'react-router-dom'
import { useState, useEffect, lazy, Suspense } from 'react'
import { auth } from './lib/api'
import { getSubdomain } from './lib/utils'
import { useUser } from './contexts/UserContext'
import DelayedSpinner from './components/DelayedSpinner'
import TrialExpiredModal from './components/TrialExpiredModal'
import TrialWarningBanner from './components/TrialWarningBanner'
import AppLayout from './components/layout/AppLayout'
import Sidebar from './components/layout/Sidebar'
import MobileHeader from './components/layout/MobileHeader'

// Eager load most common pages to eliminate loading spinner
import Home from './pages/Home'
import Login from './pages/Login'
import MagicLinkHandler from './pages/MagicLinkHandler'
import Dashboard from './pages/Dashboard'
import Pricing from './pages/Pricing'

// Lazy load less common pages for code splitting
const LeagueOverview = lazy(() => import('./pages/league/LeagueOverview'))
const Settings = lazy(() => import('./pages/Settings'))
const Users = lazy(() => import('./pages/Users'))
const PlayoffBracketView = lazy(() => import('./pages/PlayoffBracketView'))
const Organizations = lazy(() => import('./pages/Organizations'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const Billing = lazy(() => import('./pages/Billing'))
const NotificationPreferences = lazy(() => import('./pages/NotificationPreferences'))
const DiscoverTeams = lazy(() => import('./pages/DiscoverTeams'))
const JoinRequests = lazy(() => import('./pages/JoinRequests'))
const RefereeMarketplace = lazy(() => import('./pages/RefereeMarketplace'))
const RefereeDetail = lazy(() => import('./pages/RefereeDetail'))
const ScrambleLeagueManager = lazy(() => import('./pages/ScrambleLeagueManager'))
const StandingsRedirect = lazy(() => import('./pages/StandingsRedirect'))
const Inbox = lazy(() => import('./pages/Inbox'))
const AttendanceResponse = lazy(() => import('./pages/AttendanceResponse'))
const PaymentResult = lazy(() => import('./pages/PaymentResult'))
const InvitationAccept = lazy(() => import('./pages/InvitationAccept'))

// League sub-pages
const LeagueSchedule = lazy(() => import('./pages/league/LeagueSchedule'))
const LeagueStandings = lazy(() => import('./pages/league/LeagueStandings'))
const LeagueTeams = lazy(() => import('./pages/league/LeagueTeams'))
const LeagueStats = lazy(() => import('./pages/league/LeagueStats'))
const LeagueAdmin = lazy(() => import('./pages/league/LeagueAdmin'))

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

function App() {
  const { user, isAuthenticated, setUser, setIsAuthenticated, refreshUser } = useUser()

  // Trial status tracking
  const [trialStatus, setTrialStatus] = useState(null)
  const [showTrialExpired, setShowTrialExpired] = useState(false)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('logout') === 'true') {
      auth.signOut()
      setIsAuthenticated(false)
      setUser(null)
      window.history.replaceState({}, '', '/')
    }
  }, [setIsAuthenticated, setUser])

  useEffect(() => {
    const checkTrialStatus = async () => {
      if (!isAuthenticated || !user) {
        setTrialStatus(null)
        setShowTrialExpired(false)
        return
      }

      // Global admins bypass trial checks entirely
      if (user.role === 'admin') {
        return
      }

      // Only org_owners need trial status checks
      if (user.role !== 'org_owner') {
        return
      }

      try {
        const response = await fetch(`${API_URL}/api/organizations/trial-status`, {
          credentials: 'include'
        })
        if (response.ok) {
          const statuses = await response.json()
          const orgStatus = statuses.find(s => s.status === 'trialing' || s.status === 'expired')
          if (orgStatus) {
            setTrialStatus(orgStatus)
            if (orgStatus.isExpired && !orgStatus.canAccess) {
              setShowTrialExpired(true)
            }
          }
        }
      } catch (error) {
        console.error('Error checking trial status:', error)
      }
    }

    checkTrialStatus()
  }, [isAuthenticated, user])

  const handleSignOut = async () => {
    await auth.signOut()
    setIsAuthenticated(false)
    setUser(null)
    sessionStorage.clear()
    localStorage.clear()

    const hostname = window.location.hostname
    const protocol = window.location.protocol
    const port = window.location.port

    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      window.location.href = `${protocol}//localhost${port ? ':' + port : ''}/?logout=true`
    } else {
      const parts = hostname.split('.')
      const baseDomain = parts.length >= 2 ? parts.slice(-2).join('.') : hostname
      window.location.href = `${protocol}//${baseDomain}/?logout=true`
    }
  }

  return (
    <Router>
      {/* Trial Warning Banner - Shows above everything */}
      {trialStatus && trialStatus.status === 'trialing' && trialStatus.daysRemaining <= 3 && (
        <TrialWarningBanner
          daysRemaining={trialStatus.daysRemaining}
          orgName={trialStatus.name}
        />
      )}

      {/* Trial Expired Modal */}
      {showTrialExpired && trialStatus && (
        <TrialExpiredModal
          orgName={trialStatus.name}
          orgId={trialStatus.id}
          onLogout={handleSignOut}
        />
      )}

      <Suspense fallback={<DelayedSpinner delay={200} />}>
        <Routes>
          {/* Root path - handled specially based on auth and subdomain */}
          <Route path="/" element={<RootHandler />} />

          {/* Public routes - no sidebar */}
          <Route element={<PublicLayout />}>
            <Route path="pricing" element={<Pricing />} />
            <Route path="login" element={<Login user={user} onLogin={refreshUser} />} />
            <Route path="auth/magic-link" element={<MagicLinkHandler />} />
            <Route path="invitation/accept" element={<InvitationAccept />} />
            <Route path="attendance/:gameId" element={<AttendanceResponse />} />
            <Route path="payment/success" element={<PaymentResult />} />
            <Route path="payment/cancel" element={<PaymentResult />} />
          </Route>

          {/* Protected routes - with sidebar */}
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="admin" element={<AdminDashboard />} />
            <Route path="admin/organizations" element={<AdminDashboard />} />
            <Route path="admin/users" element={<AdminDashboard />} />
            <Route path="admin/billing" element={<AdminDashboard />} />
            <Route path="admin/notifications" element={<AdminDashboard />} />
            <Route path="admin/system-alerts" element={<AdminDashboard />} />
            <Route path="leagues/:id" element={<LeagueOverview />} />
            <Route path="settings" element={<Settings />} />
            <Route path="billing" element={<Billing user={user} />} />
            <Route path="users" element={<Users />} />
            <Route path="broadcasts" element={<Navigate to="/messages" replace />} />
            <Route path="notifications" element={<NotificationPreferences />} />
            <Route path="discover-teams" element={<DiscoverTeams />} />
            <Route path="join-requests" element={<JoinRequests />} />
            <Route path="playoffs/:bracketId/view" element={<PlayoffBracketView />} />
            <Route path="referees" element={<RefereeMarketplace />} />
            <Route path="referees/:id" element={<RefereeDetail />} />
            <Route path="leagues/:id/scramble" element={<ScrambleLeagueManager />} />
            <Route path="standings" element={<StandingsRedirect />} />
            <Route path="inbox" element={<Inbox />} />
            <Route path="inbox/:conversationId" element={<Inbox />} />

            {/* League sub-pages (by ID) */}
            <Route path="leagues/:id/schedule" element={<LeagueSchedule />} />
            <Route path="leagues/:id/standings" element={<LeagueStandings />} />
            <Route path="leagues/:id/teams" element={<LeagueTeams />} />
            <Route path="leagues/:id/stats" element={<LeagueStats />} />
            <Route path="leagues/:id/inbox" element={<Inbox />} />
            <Route path="leagues/:id/inbox/:conversationId" element={<Inbox />} />
            <Route path="leagues/:id/join-requests" element={<JoinRequests />} />
            <Route path="leagues/:id/admin" element={<LeagueAdmin />} />

            {/* Slug-based league routes */}
            <Route path=":leagueSlug" element={<LeagueOverview />} />
            <Route path=":leagueSlug/schedule" element={<LeagueSchedule />} />
            <Route path=":leagueSlug/standings" element={<LeagueStandings />} />
            <Route path=":leagueSlug/teams" element={<LeagueTeams />} />
            <Route path=":leagueSlug/stats" element={<LeagueStats />} />
            <Route path=":leagueSlug/inbox" element={<Inbox />} />
            <Route path=":leagueSlug/inbox/:conversationId" element={<Inbox />} />
            <Route path=":leagueSlug/join-requests" element={<JoinRequests />} />
            <Route path=":leagueSlug/admin" element={<LeagueAdmin />} />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  )
}

/**
 * PublicLayout - Simple layout for unauthenticated pages
 */
function PublicLayout() {
  const location = useLocation()
  const { isAuthenticated } = useUser()
  const isLoginPage = location.pathname === '/login'
  const isHomePage = location.pathname === '/'

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Simple top navigation for public pages */}
      <nav className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/shield-logo.png"
                alt="IceOps Shield"
                className="h-10 w-auto"
                fetchpriority="high"
              />
              <span className="text-xl font-bold text-white">IceOps</span>
            </Link>

            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <Link to="/dashboard" className="btn-primary">
                  Go to Dashboard
                </Link>
              ) : (
                !isLoginPage && (
                  <Link to="/login" className="btn-primary">
                    Sign In
                  </Link>
                )
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className={isHomePage ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        <Outlet />
      </main>
    </div>
  )
}

/**
 * ProtectedRoute - Redirects to login if not authenticated
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useUser()
  const location = useLocation()

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return children
}

/**
 * RootHandler - Decides what to show at the root path based on auth and subdomain
 *
 * - Main domain (iceops.app) unauthenticated → Public Home (marketing)
 * - Main domain authenticated → Redirect to /dashboard
 * - Subdomain (test.iceops.app) unauthenticated → Public league view
 * - Subdomain authenticated → AppLayout with league view (sidebar)
 */
function RootHandler() {
  const { isAuthenticated } = useUser()
  const subdomain = getSubdomain()

  // On main domain
  if (!subdomain) {
    if (isAuthenticated) {
      return <Navigate to="/dashboard" replace />
    }
    // Show public marketing home with simple nav
    return (
      <div className="min-h-screen bg-slate-100">
        <nav className="bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-2">
                <img src="/shield-logo.png" alt="IceOps" className="h-10 w-auto" />
                <span className="text-xl font-bold text-white">IceOps</span>
              </Link>
              <Link to="/login" className="btn-primary">Sign In</Link>
            </div>
          </div>
        </nav>
        <Home />
      </div>
    )
  }

  // On subdomain - authenticated users get sidebar layout
  if (isAuthenticated) {
    return <AuthenticatedSubdomainLayout />
  }

  // Unauthenticated on subdomain - show public league view
  return (
    <div className="min-h-screen bg-slate-100">
      <nav className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/shield-logo.png" alt="IceOps" className="h-10 w-auto" />
              <span className="text-xl font-bold text-white">IceOps</span>
            </Link>
            <Link to="/login" className="btn-primary">Sign In</Link>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Home />
      </main>
    </div>
  )
}

/**
 * AuthenticatedSubdomainLayout - Shows Home content with sidebar for authenticated subdomain users
 */
function AuthenticatedSubdomainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />
      <main className="lg:ml-64 min-h-screen">
        <div className="h-14 lg:h-0" />
        <div className="p-4 lg:p-6">
          <Home />
        </div>
      </main>
    </div>
  )
}

export default App
